import React, { useEffect, useRef } from 'react';

export function AuthBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) return;

    let animationFrameId;
    let width, height, cx, cy, maxDim;
    let time = 0;

    let mouseX = -1, mouseY = -1;
    let targetMouseX = -1, targetMouseY = -1;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      cx = width / 2;
      cy = height / 2;
      maxDim = Math.sqrt(cx * cx + cy * cy);
    };

    window.addEventListener('resize', resize);
    resize();

    const onMouse = (e) => {
      targetMouseX = e.clientX;
      targetMouseY = e.clientY;
    };
    window.addEventListener('mousemove', onMouse);

    const spacing = 48;
    const h = spacing * Math.sin(Math.PI / 3);

    const draw = () => {
      time += 1;

      if (targetMouseX === -1) {
        targetMouseX = cx;
        targetMouseY = cy;
        mouseX = cx;
        mouseY = cy;
      }

      mouseX += (targetMouseX - mouseX) * 0.035;
      mouseY += (targetMouseY - mouseY) * 0.035;

      // Deep background
      ctx.fillStyle = '#06060e';
      ctx.globalAlpha = 1;
      ctx.fillRect(0, 0, width, height);

      // Subtle background gradient wash — purple top-left, blue bottom-right
      const bgWash = ctx.createLinearGradient(0, 0, width, height);
      bgWash.addColorStop(0, 'rgba(152,96,167,0.04)');
      bgWash.addColorStop(0.5, 'rgba(6,6,14,0)');
      bgWash.addColorStop(1, 'rgba(38,58,149,0.03)');
      ctx.fillStyle = bgWash;
      ctx.fillRect(0, 0, width, height);

      const cols = Math.ceil((maxDim * 1.6) / spacing) + 1;
      const rows = Math.ceil((maxDim * 1.6) / h) + 1;

      ctx.save();
      ctx.translate(cx, cy);

      const rot = time * 0.00025;
      ctx.rotate(rot);

      // Mouse in rotated space
      const dx = mouseX - cx;
      const dy = mouseY - cy;
      const lmx = dx * Math.cos(-rot) - dy * Math.sin(-rot);
      const lmy = dx * Math.sin(-rot) + dy * Math.cos(-rot);

      // Collect vertex positions for node highlights
      const nearVertices = [];

      for (let row = -rows; row <= rows; row++) {
        for (let col = -cols; col <= cols; col++) {
          const x = col * spacing + (Math.abs(row) % 2 === 1 ? spacing / 2 : 0);
          const y = row * h;

          const distC = Math.sqrt(x * x + y * y);
          if (distC > maxDim * 1.6) continue;

          // Distance from mouse
          const dmx = x - lmx;
          const dmy = y - lmy;
          const distM = Math.sqrt(dmx * dmx + dmy * dmy);

          const angle = Math.atan2(dmy, dmx);
          const sym = (angle * 6) % (Math.PI * 2);

          // Layered wave — slow primary + faster harmonic
          const wave1 = Math.sin(distM * 0.006 - time * 0.01) * Math.cos(sym + time * 0.003);
          const wave2 = Math.sin(distM * 0.012 + time * 0.005) * 0.3;
          const wave = wave1 + wave2 * 0.4;

          // Mouse proximity — tighter, more dramatic falloff
          const proximity = Math.max(0, 1 - distM / (maxDim * 0.35));
          const proxSq = proximity * proximity;

          // Radial fade from center — grid brighter near center
          const centerFade = Math.max(0.3, 1 - distC / (maxDim * 1.2));

          // Final line intensity
          const intensity = Math.min(0.55, Math.max(0.03,
            (0.06 + wave * 0.2 + proxSq * 0.25) * centerFade
          ));

          // Color: radial gradient — purple near center, blue at edges
          const colorBlend = Math.min(1, distC / (maxDim * 0.9));
          const r = Math.round(152 - colorBlend * 114);
          const g = Math.round(96 - colorBlend * 38);
          const b = Math.round(167 - colorBlend * 18);

          ctx.globalAlpha = intensity;
          ctx.strokeStyle = `rgb(${r},${g},${b})`;
          ctx.lineWidth = 0.6 + proxSq * 0.6;

          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.lineTo(x + spacing, y);
          ctx.moveTo(x, y);
          ctx.lineTo(x + spacing / 2, y + h);
          ctx.moveTo(x, y);
          ctx.lineTo(x - spacing / 2, y + h);
          ctx.stroke();

          // Collect vertices near mouse for node highlights
          if (distM < 220) {
            nearVertices.push({ x, y, distM, intensity });
          }
        }
      }

      // Draw glowing vertex dots near cursor
      for (const v of nearVertices) {
        const nodeFade = Math.max(0, 1 - v.distM / 220);
        const nodeAlpha = nodeFade * nodeFade * 0.6;
        const nodeSize = 1 + nodeFade * 2;

        ctx.globalAlpha = nodeAlpha;
        ctx.fillStyle = 'rgba(200,180,220,1)';
        ctx.beginPath();
        ctx.arc(v.x, v.y, nodeSize, 0, Math.PI * 2);
        ctx.fill();

        // Tiny glow halo around bright nodes
        if (nodeFade > 0.5) {
          ctx.globalAlpha = nodeAlpha * 0.3;
          ctx.beginPath();
          ctx.arc(v.x, v.y, nodeSize * 3, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      ctx.restore();

      // Ambient glow following mouse — stronger, warmer
      ctx.globalAlpha = 1;
      const glow = ctx.createRadialGradient(mouseX, mouseY, 0, mouseX, mouseY, maxDim * 0.5);
      glow.addColorStop(0, 'rgba(152,96,167,0.10)');
      glow.addColorStop(0.25, 'rgba(120,80,160,0.05)');
      glow.addColorStop(0.5, 'rgba(38,58,149,0.03)');
      glow.addColorStop(1, 'rgba(6,6,14,0)');
      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, width, height);

      // Vignette — softer, less heavy
      ctx.globalAlpha = 1;
      const vig = ctx.createRadialGradient(cx, cy, maxDim * 0.4, cx, cy, maxDim * 1.1);
      vig.addColorStop(0, 'rgba(6,6,14,0)');
      vig.addColorStop(1, 'rgba(6,6,14,0.35)');
      ctx.fillStyle = vig;
      ctx.fillRect(0, 0, width, height);
    };

    const animate = () => {
      draw();
      animationFrameId = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMouse);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none',
      }}
    />
  );
}

export default AuthBackground;
